import { UiLink } from '../labels';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';

export interface SectionNavItem {
  href: string;
  label: string;
  icon?: IconName;
  active?: boolean;
}

export interface SectionNavGroup {
  label?: string;
  items: readonly SectionNavItem[];
}

/** Vertical navigation inside an area with several pages, e.g. the administration. */
export function SectionNav({
  label,
  groups,
  className,
}: {
  /** Accessible name of the navigation. */
  label: string;
  groups: readonly SectionNavGroup[];
  className?: string;
}) {
  return (
    <nav aria-label={label} className={cn('flex flex-col gap-4', className)}>
      {groups.map((group, index) => (
        <div key={group.label ?? index}>
          {group.label && (
            <h2 className="px-2.5 pb-1.5 text-xs font-medium text-fg-subtle">{group.label}</h2>
          )}
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <li key={item.href}>
                <UiLink
                  href={item.href}
                  aria-current={item.active ? 'page' : undefined}
                  className={cn(
                    'flex h-control items-center gap-2.5 rounded-md px-2.5 text-fg-muted transition-colors transition-fast hover:bg-hover hover:text-fg',
                    item.active && 'bg-brand-subtle font-medium text-fg hover:bg-brand-subtle',
                  )}
                >
                  {item.icon && (
                    <Icon name={item.icon} className={cn(item.active && 'text-brand-text')} />
                  )}
                  <span className="truncate">{item.label}</span>
                </UiLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
